import { useEffect, useRef } from 'react';

/**
 * Hook para fazer logout automático após período de inatividade
 * @param {number} timeoutMinutes - Tempo em minutos para logout automático (padrão: 30)
 * @param {function} onLogout - Função callback para executar o logout (obrigatória)
 */
export function useAutoLogout(timeoutMinutes = 30, onLogout) {
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Função para resetar o timer
  const resetTimer = () => {
    // Limpar timer anterior
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Atualizar última atividade
    lastActivityRef.current = Date.now();

    // Criar novo timer
    const timeoutMs = timeoutMinutes * 60 * 1000; // Converter minutos para milissegundos
    timeoutRef.current = setTimeout(() => {
      // Executar logout
      if (onLogout && typeof onLogout === 'function') {
        onLogout();
      } else {
        // Logout padrão se não houver callback válido
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('logo_path');
        window.location.href = '/login';
      }
    }, timeoutMs);
  };

  // Função para detectar atividade do usuário
  const handleActivity = () => {
    resetTimer();
  };

  useEffect(() => {
    // Verificar se há token (usuário logado)
    const token = localStorage.getItem('token');
    if (!token) {
      return; // Se não estiver logado, não precisa do timer
    }

    // Iniciar o timer
    resetTimer();

    // Eventos que indicam atividade do usuário
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    // Adicionar listeners para todos os eventos
    events.forEach(event => {
      window.addEventListener(event, handleActivity, true);
    });

    // Cleanup: remover listeners e limpar timer
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity, true);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [timeoutMinutes, onLogout]);

  // Retornar função para resetar manualmente (útil para requisições)
  return { resetTimer };
}
